export class Modal {

    static #focusTrap = typeof FocusTrap !== 'undefined';
    static #isSupported = typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function';
    static #instances = new Map();
    static #globalEventListenersAdded = false;

    static {
        if (!Modal.#focusTrap) {
            console.warn('FocusTrap is not defined. Focus trapping will be disabled.');
        }
        if (!Modal.#isSupported) {
            console.warn('The <dialog> element is not fully supported in this browser.');
        }
        if (!Modal.#globalEventListenersAdded) {
            Modal.#addGlobalEventListeners();
            Modal.#globalEventListenersAdded = true;
        }
    }

    constructor(settings = {}) {
        const {
            modal,
            hasCloseButton = true,
            startsOpen = false,
            allowClosing = true,
            allowDeeplinking = true,
            allowHistory = true,
            on = {}
        } = settings;

        if (typeof modal === 'string') {
            this.modal = document.querySelector(`#${modal}`);
        } else if (modal instanceof HTMLElement) {
            this.modal = modal;
        } else {
            console.warn('Invalid modal parameter. Expected string or HTMLElement.');
            return;
        }

        if (!this.modal) {
            console.warn(`Modal element not found for selector or element: ${modal}`);
            return;
        }

        if (Modal.#instances.has(this.modal)) {
            console.warn('Modal instance already exists for the provided element.');
            return;
        }

        this.hasCloseButton = hasCloseButton;
        this.startsOpen = startsOpen;
        this.allowClosing = allowClosing;
        this.allowDeeplinking = allowDeeplinking;
        this.allowHistory = allowHistory;
        this.isManuallyClosed = false;

        this.on = {
            beforeOpen: on.beforeOpen || (() => {}),
            beforeClose: on.beforeClose || (() => {}),
            open: on.open || (() => {}),
            close: on.close || (() => {}),
            ready: on.ready || (() => {}),
            destroy: on.destroy || (() => {})
        };

        this.eventListeners = [];
        this.isTriggered = false;

        Modal.#instances.set(this.modal, this);

        this.init();
    }

    init() {
        this.setAttributes();
        this.buildHtmlMarkup();
        this.addEventListeners();

        if (typeof this.on.ready === 'function' && this.on.ready.call(this, this.modal, this) === false) {
            return;
        }

        if (this.startsOpen) {
            setTimeout(() => this.open(false));
        }

        if (this.allowDeeplinking) {
            this.openFromHistoryState();
        }
    }

    setAttributes() {
        this.modal.setAttribute('aria-modal', true);
        this.modal.setAttribute('role', 'dialog');
        if (!this.modal.hasAttribute('aria-labelledby') && this.modal.id) {
            this.modal.setAttribute('aria-labelledby', `${this.modal.id}-title`);
        }
    }

    buildHtmlMarkup() {
        if (Modal.#focusTrap) {
            FocusTrap.buildFocusBumperElements(this.modal);
        }
    }

    addEventListeners() {
        const clickHandler = (event) => {
            if (this.hasCloseButton && this.allowClosing && (event.target === this.modal || event.target.matches('.dialog-container'))) {
                this.close();
            }
        };

        const handleOpen = () => {
            if (Modal.#focusTrap) {
                FocusTrap.start(this.modal);
            }
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
            document.body.classList.add('modal-open');
            if (typeof this.on.open === 'function') {
                this.on.open.call(this, this.modal, this);
            }
        };

        const handleClose = () => {
            if (typeof this.on.close === 'function') {
                this.on.close.call(this, this.modal, this);
            }
            if (Modal.#focusTrap) {
                FocusTrap.stop(this.modal);
            }
            if (!Modal.anyIsOpen()) {
                document.body.classList.remove('modal-open');
                document.documentElement.style.removeProperty('--scrollbar-width');
            }
        };

        this.eventListeners.push(
            { element: this.modal, type: 'click', handler: clickHandler },
            { element: this.modal, type: 'modal-close', handler: handleClose },
            { element: this.modal, type: 'modal-open', handler: handleOpen }
        );

        this.eventListeners.forEach(({ element, type, handler }) => {
            element.addEventListener(type, handler, true);
        });
    }

    toggle() {
        if (this.modal.open) {
            this.close();
        } else {
            this.open();
        }
    }

    close() {
        if (!this.modal.open) return;
        if (typeof this.on.beforeClose === 'function' && this.on.beforeClose.call(this, this.modal, this) === false) return;

        this.modal.close();
        this.modal.dispatchEvent(new CustomEvent('modal-close', {
            bubbles: true,
            detail: { modal: this.modal }
        }));

        this.setHistoryState();
    }

    open(closeOthers = true) {
        if (typeof this.on.beforeOpen === 'function' && this.on.beforeOpen.call(this, this.modal, this) === false) return;
        if (closeOthers) Modal.closeAll();

        this.modal.showModal();
        setTimeout(() => {
            this.modal.scrollTop = 0;
            const innerContent = this.modal.querySelector('.dialog-inner');
            if (innerContent) innerContent.scrollTop = 0;
        });
        this.modal.dispatchEvent(new CustomEvent('modal-open', {
            bubbles: true,
            detail: { modal: this.modal }
        }));

        this.setHistoryState();
    }

    destroy(removeFromDOM = false) {
        this.modal.close();
        this.setHistoryState();
        this.eventListeners.forEach(({ element, type, handler }) => {
            element.removeEventListener(type, handler, true);
        });

        Modal.#instances.delete(this.modal);
        if (!Modal.anyIsOpen()) {
            document.body.classList.remove('modal-open');
            document.documentElement.style.removeProperty('--scrollbar-width');
        }

        if (removeFromDOM) {
            this.modal.remove();
        }

        if (typeof this.on.destroy === 'function') this.on.destroy.call(this, this.modal, this);
    }

    setHistoryState() {
        if (window.HashManager && (this.allowHistory || !this.modal.open)) {
            this.isTriggered = true;
            window.HashManager.updateState({ [this.modal.id]: this.modal.open });
        }
    }

    openFromHistoryState() {
        if (window.HashManager) {
            const state = Object.hasOwn(window.HashManager.state, this.modal.id) || false;
            if (state) {
                setTimeout(() => {
                    this.open(false);
                },50);
            }
        }
    }

    static open(modal, closeOthers = true) {
        const instance = Modal.#instances.get(modal);
        if (!instance) return console.warn('Modal not found.');
        instance.open(closeOthers);
    }

    static close(modal) {
        const instance = Modal.#instances.get(modal);
        if (!instance) return console.warn('Modal not found.');
        instance.close();
    }

    static closeAll() {
        Modal.#instances.forEach(instance => {
            if (instance.allowClosing && instance.modal.open) instance.close();
        });
    }

    static isOpen(modal) {
        const instance = Modal.#instances.get(modal);
        return instance ? instance.modal.hasAttribute('open') : false;
    }

    static anyIsOpen() {
        return Array.from(Modal.#instances.values()).some(instance => instance.modal.hasAttribute('open'));
    }

    static destroy(modal, removeFromDOM = false) {
        const instance = Modal.#instances.get(modal);
        if (!instance) return console.warn('Modal not found for destruction.');
        instance.destroy(removeFromDOM);
    }

    static #addGlobalEventListeners() {
        document.addEventListener('click', (event) => {
            const target = event.target;
            const id = target.getAttribute('aria-controls');
            if (id) {
                const modalElement = document.getElementById(id);
                const instance = Modal.#instances.get(modalElement);
                if (instance) {
                    event.preventDefault();
                    if (!instance.isManuallyClosed) {
                        instance.toggle();
                    } else {
                        instance.isManuallyClosed = false;
                    }
                }
            }
        }, true);

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                Modal.#instances.forEach(instance => {
                    if (instance.modal.open && instance.allowClosing) instance.close();
                });
                event.preventDefault();
            }
        }, true);

        // window.addEventListener('hashchange', Modal.#debounce(Modal.#historyStateHandler, 100));
        window.addEventListener('popstate', Modal.#debounce(Modal.#historyStateHandler, 100));
    }

    static #historyStateHandler() {
        if (window.HashManager) {
            const states = window.HashManager.state;
            Modal.#instances.forEach(instance => {
                if (!instance.isTriggered && instance.allowDeeplinking) {
                    if (Object.hasOwn(states, instance.modal.id)) {
                        instance.open(false);
                    } else if (instance.modal.open && instance.allowClosing) {
                        instance.close();
                    }
                }
                if (instance.isTriggered) instance.isTriggered = false;
            });
        }
    }

    static #debounce(func, delay) {
        let timer;
        return function(...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    }
}

window.Modal = Modal;